import { FormOutlined, PlusOutlined, UserAddOutlined } from "@ant-design/icons";
import { Emoji } from "@dewo/app/components/emoji/Emoji";
import { OrganizationIntegrationType, Role } from "@dewo/app/graphql/types";
import { Button, Col, Divider, List, Row, Space, Typography } from "antd";
import React, { FC, useState } from "react";
import { ConnectOrganizationToDiscordButton } from "../../integrations/discord/ConnectOrganizationToDiscordButton";
import { useOrganizationIntegrations } from "../../organization/hooks";
import { NewRoleButton } from "../../organization/roles/NewRoleButton";
import { RoleModal } from "../../organization/roles/RoleModal";
import { TaskBoardColumnEmpty } from "../../task/board/TaskBoardColumnEmtpy";
import { useOrganizationRoleDetails } from "../hooks";

interface Props {
  organizationId: string;
}

export const OrganizationRoles: FC<Props> = ({ organizationId }) => {
  const { roles } = useOrganizationRoleDetails(organizationId);
  const allRoles = roles?.filter((r) => !r.fallback && !r.userId);
  const [editedRole, setEditedRole] = useState<Role | undefined>();

  const discordConnected = !!useOrganizationIntegrations(
    organizationId,
    OrganizationIntegrationType.DISCORD
  )?.length;

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Roles
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <ConnectOrganizationToDiscordButton
              hidden={discordConnected}
              organizationId={organizationId}
              children="Import from Discord"
            />
            <NewRoleButton
              type="primary"
              icon={<PlusOutlined />}
              organizationId={organizationId}
              onSubmit={() => {}}
            >
              New role
            </NewRoleButton>
          </Space>
        </Col>
      </Row>
      <Divider style={{ marginTop: 8 }} />
      <List
        children={
          !allRoles?.length ? (
            <TaskBoardColumnEmpty
              title={
                <>
                  <Typography.Paragraph type="secondary">
                    Connect to Discord or create a new role to control access
                  </Typography.Paragraph>

                  <Space>
                    <ConnectOrganizationToDiscordButton
                      hidden={discordConnected}
                      organizationId={organizationId}
                      children="Import from Discord"
                    />
                    <NewRoleButton
                      type="primary"
                      icon={<PlusOutlined />}
                      organizationId={organizationId}
                      onSubmit={() => {}}
                    >
                      New role
                    </NewRoleButton>
                  </Space>
                </>
              }
              icon={<UserAddOutlined />}
            />
          ) : null
        }
        loading={!allRoles}
        bordered
        dataSource={allRoles}
        renderItem={(r) => (
          <List.Item key={r.id}>
            <Row gutter={8} style={{ width: "100%" }}>
              <Col style={{ width: "1.4em" }}>
                {!!r.icon && <Emoji name={r.icon} />}
              </Col>
              <Col flex={1}>{r.name}</Col>
              <Col hidden={!r.userCount}>
                <Typography.Text type="secondary">
                  {r.userCount !== 1 ? `${r.userCount} members` : "1 member"}
                </Typography.Text>
              </Col>
              <Col>
                <Button
                  icon={<FormOutlined />}
                  type="text"
                  size="small"
                  onClick={() => setEditedRole(r)}
                />
              </Col>
            </Row>
          </List.Item>
        )}
      />
      <RoleModal
        organizationId={organizationId}
        visible={!!editedRole}
        role={editedRole}
        mode="update"
        onSubmit={() => setEditedRole(undefined)}
        onCancel={() => setEditedRole(undefined)}
      />
    </>
  );
};
